import {
  createApi,
  fetchBaseQuery,
  type BaseQueryFn,
  type FetchArgs,
  type FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';

import { config } from '@/app/config/env';
import { logger } from '@/lib/logger';

import { mockBaseQuery } from './mockBaseQuery';

/**
 * The single HTTP client for the application.
 *
 * All server state flows through this API slice. UI components never call
 * `fetch` directly — centralizing it here is what makes caching, auth
 * handling, retries, and error normalization consistent instead of
 * reimplemented per call site.
 *
 * AUTHENTICATION MODEL
 * --------------------
 * Session credentials are carried in HttpOnly, Secure, SameSite cookies set by
 * the backend, and never read or stored by this client. That choice is
 * deliberate: a token held in JavaScript-reachable storage is exfiltrable by
 * any XSS flaw, whereas an HttpOnly cookie is not. Consequently:
 *
 * - `credentials: 'include'` lets the browser attach the session cookie.
 * - No `Authorization` header is constructed here.
 * - Because cookies are sent automatically, the backend must enforce CSRF
 *   protection. This client forwards the double-submit token when one is
 *   present (see `readCsrfToken`).
 */

/** Tag types used for cache invalidation across feature endpoint slices. */
export const API_TAGS = ['Auth', 'User', 'AgencyOverview', 'Account', 'Invoice'] as const;
export type ApiTag = (typeof API_TAGS)[number];

/** Name of the non-HttpOnly cookie holding the CSRF double-submit token. */
const CSRF_COOKIE_NAME = 'XSRF-TOKEN';
const CSRF_HEADER_NAME = 'X-XSRF-TOKEN';

/** Methods that mutate state and therefore require a CSRF token. */
const UNSAFE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/**
 * Reads the CSRF token the backend exposes as a readable cookie.
 *
 * This cookie intentionally is *not* HttpOnly — the double-submit pattern
 * requires JavaScript to echo it back in a header, which a cross-site attacker
 * cannot do because they cannot read the victim's cookies.
 */
function readCsrfToken(): string | null {
  if (typeof document === 'undefined') return null;

  const match = document.cookie
    .split('; ')
    .find((entry) => entry.startsWith(`${CSRF_COOKIE_NAME}=`));

  if (!match) return null;

  const value = match.slice(CSRF_COOKIE_NAME.length + 1);
  try {
    return decodeURIComponent(value);
  } catch {
    return null;
  }
}

const rawBaseQuery = fetchBaseQuery({
  baseUrl: config.apiBaseUrl,
  // Send the session cookie with same-site/CORS-approved requests.
  credentials: 'include',
  timeout: config.apiTimeoutMs,
  prepareHeaders: (headers, { arg }) => {
    headers.set('Accept', 'application/json');

    // Marks the request as programmatic, letting the backend answer with 401
    // JSON instead of an HTML login redirect.
    headers.set('X-Requested-With', 'XMLHttpRequest');

    // Attach the CSRF token to state-changing requests only. Keying off the
    // HTTP method rather than the RTK operation type is what the backend
    // actually checks, and keeps a mutation issued as a GET from being sent
    // a token it does not need.
    const method = typeof arg === 'object' && arg.method ? arg.method.toUpperCase() : 'GET';

    if (UNSAFE_METHODS.has(method)) {
      const csrfToken = readCsrfToken();
      if (csrfToken) {
        headers.set(CSRF_HEADER_NAME, csrfToken);
      }
    }

    return headers;
  },
});

/**
 * Dispatched when the backend rejects a request as unauthenticated.
 *
 * Declared as a plain action type rather than importing the auth slice, so the
 * transport layer stays independent of feature code — the auth slice listens
 * for this instead. This keeps the dependency direction one-way.
 */
export const SESSION_EXPIRED = 'auth/sessionExpired' as const;

/**
 * Wraps the base query to handle session expiry once, centrally, rather than
 * in every component that happens to make a request.
 */
const baseQueryWithAuthHandling: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  // `mockBaseQuery` serves fixture data for endpoints with no backend yet;
  // it never talks to the network, so auth/session handling below is moot
  // for those requests and this short-circuits before it.
  if (config.useMockApi) {
    return mockBaseQuery(args, api, extraOptions);
  }

  const result = await rawBaseQuery(args, api, extraOptions);

  if (result.error?.status === 401) {
    // The session is gone. Clear client-side auth state so guarded routes stop
    // rendering; the backend has already invalidated the cookie.
    api.dispatch({ type: SESSION_EXPIRED });

    logger.warn('Request rejected as unauthenticated', {
      endpoint: api.endpoint,
    });
  }

  return result;
};

/**
 * Adds bounded retries for genuinely transient failures.
 *
 * Retries are limited to idempotent reads: replaying a mutation risks
 * duplicating a side effect the server already applied.
 */
const MAX_RETRIES = 2;

const baseQueryWithRetry: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions,
) => {
  let result = await baseQueryWithAuthHandling(args, api, extraOptions);

  const isQuery = api.type === 'query';
  let attempt = 0;

  while (isQuery && attempt < MAX_RETRIES && isTransientFailure(result.error)) {
    attempt += 1;
    // Exponential backoff keeps a struggling backend from being hammered.
    await delay(2 ** attempt * 150);
    result = await baseQueryWithAuthHandling(args, api, extraOptions);
  }

  return result;
};

function isTransientFailure(error: FetchBaseQueryError | undefined): boolean {
  if (!error) return false;
  if (error.status === 'FETCH_ERROR' || error.status === 'TIMEOUT_ERROR') return true;
  return typeof error.status === 'number' && error.status >= 500;
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/**
 * Root API slice. Feature modules extend this with `injectEndpoints` so that
 * their endpoint definitions are code-split alongside the feature itself
 * rather than all being pulled into the initial bundle.
 */
export const baseApi = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithRetry,
  tagTypes: API_TAGS,
  // Endpoints are injected by features; none are defined centrally.
  endpoints: () => ({}),
  // Refetch stale data when the user returns to the tab or regains network.
  refetchOnFocus: true,
  refetchOnReconnect: true,
  keepUnusedDataFor: 60,
});
